import { cn } from "@/lib/utils";
import { Eye, EyeOff, GripVertical, Search } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";

// Type pour représenter un champ depuis le backend Django
interface FieldInfo {
    type: string;
    required: boolean;
    read_only: boolean;
    label: string;
    max_length?: number;
    min_value?: number;
    max_value?: number;
    choices?: Array<{ value: string; display_name: string }>;
}

interface ColumnConfigModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    endpointUrl?: string; // URL de l'endpoint pour la requête OPTIONS
    defaultVisibleFields?: string[];
    visibleColumns: string[];
    onVisibleColumnsChange: (columns: string[]) => void;
    onColumnsManuallyModified?: () => void; // Callback when columns are manually modified
    fieldsInfo: Record<string, FieldInfo> | null;
}

// Fonction pour obtenir le type d'un champ depuis le backend
const getFieldType = (fieldName: string, fieldsInfo?: Record<string, FieldInfo> | null): string => {
    return fieldsInfo?.[fieldName]?.type || "string";
};

// Fonction pour obtenir le label d'un champ depuis le backend
const getFieldLabel = (fieldName: string, fieldsInfo?: Record<string, FieldInfo> | null): string => {
    return fieldsInfo?.[fieldName]?.label || fieldName;
};

// Fonction pour obtenir tous les champs disponibles depuis le backend
const getAllFields = (fieldsInfo?: Record<string, FieldInfo> | null): string[] => {
    if (!fieldsInfo) return [];
    return Object.keys(fieldsInfo).sort();
};

export function ColumnConfigModal({ open, onOpenChange, defaultVisibleFields, visibleColumns, onVisibleColumnsChange, onColumnsManuallyModified, fieldsInfo }: ColumnConfigModalProps) {
    const [searchField, setSearchField] = useState("");
    const [draggedItem, setDraggedItem] = useState<string | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    // Internal state to track changes before applying them
    const [tempVisibleColumns, setTempVisibleColumns] = useState<string[]>(visibleColumns);

    // Obtenir tous les champs disponibles
    const allFields = getAllFields(fieldsInfo);

    // Reset temp state when modal opens or visibleColumns prop changes
    useEffect(() => {
        setTempVisibleColumns(visibleColumns);
    }, [visibleColumns, open]);

    // Fonction pour basculer la visibilité d'une colonne (temp state only)
    const toggleColumnVisibility = (field: string) => {
        const newColumns = tempVisibleColumns.includes(field) ? tempVisibleColumns.filter(col => col !== field) : [...tempVisibleColumns, field];
        setTempVisibleColumns(newColumns);
    };

    // Fonctions de drag & drop (temp state only)
    const handleDragStart = (e: React.DragEvent, field: string) => {
        setDraggedItem(field);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();

        if (draggedItem) {
            const dragIndex = tempVisibleColumns.indexOf(draggedItem);
            if (dragIndex !== -1) {
                const newOrder = [...tempVisibleColumns];
                newOrder.splice(dragIndex, 1);
                newOrder.splice(dropIndex, 0, draggedItem);
                setTempVisibleColumns(newOrder);
            }
        }

        setDraggedItem(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
        setDragOverIndex(null);
    };

    // Handle validate button click
    const handleValidate = () => {
        onVisibleColumnsChange(tempVisibleColumns);
        onColumnsManuallyModified?.(); // Signal that columns were manually modified
        onOpenChange(false);
    };

    // Handle cancel button click
    const handleCancel = () => {
        setTempVisibleColumns(visibleColumns); // Reset to original state
        onOpenChange(false);
    };

    // Filtrage des champs selon la recherche
    const filteredFields = allFields.filter(field => getFieldLabel(field, fieldsInfo).toLowerCase().includes(searchField.toLowerCase()) || field.toLowerCase().includes(searchField.toLowerCase()));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-6xl">
                <DialogHeader>
                    <DialogTitle>Update columns</DialogTitle>
                    <DialogDescription>Manage the display and order of the columns of the table.</DialogDescription>
                </DialogHeader>
                <div>
                    <div className="grid grid-cols-2 gap-6 py-4">
                        {/* Left column: Select fields */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold">Available fields</h3>
                                <Badge variant="secondary">{`${allFields.length} fields`}</Badge>
                            </div>

                            {/* Recherche */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                                <Input placeholder="Search a field..." value={searchField} onChange={e => setSearchField(e.target.value)} className="pl-10" />
                            </div>

                            {/* Liste des champs */}
                            <ScrollArea className="h-[400px] border-b pr-4">
                                <div className="space-y-1">
                                    {filteredFields.map(field => (
                                        <div key={field} className="flex items-center space-x-2 rounded p-2 hover:bg-gray-50">
                                            <Checkbox checked={tempVisibleColumns.includes(field)} onCheckedChange={() => toggleColumnVisibility(field)} />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-sm font-medium">{getFieldLabel(field, fieldsInfo)}</span>
                                                    <Badge variant="outline" className="text-xs">
                                                        {getFieldType(field, fieldsInfo)}
                                                    </Badge>
                                                    {fieldsInfo && fieldsInfo[field]?.required && (
                                                        <Badge variant="destructive" className="text-xs">
                                                            Required
                                                        </Badge>
                                                    )}
                                                    {fieldsInfo && fieldsInfo[field]?.read_only && (
                                                        <Badge variant="secondary" className="text-xs">
                                                            Read only
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="truncate text-xs text-gray-500">{field}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        </div>

                        {/* Colonne droite : Colonnes actives */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold">Active columns</h3>
                                <Badge variant="secondary">{tempVisibleColumns.length} columns</Badge>
                            </div>

                            <ScrollArea className="h-[450px] border-b pr-4">
                                <div className="space-y-2">
                                    {tempVisibleColumns.map((field, index) => (
                                        <div
                                            key={field}
                                            className={cn(
                                                "flex items-center space-x-3 rounded border bg-gray-50 p-3 transition-all duration-200",
                                                dragOverIndex === index && "border-blue-500 bg-blue-50",
                                                draggedItem === field && "opacity-50"
                                            )}
                                            draggable
                                            onDragStart={e => handleDragStart(e, field)}
                                            onDragOver={e => handleDragOver(e, index)}
                                            onDragLeave={handleDragLeave}
                                            onDrop={e => handleDrop(e, index)}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <div className="flex cursor-grab items-center space-x-2 active:cursor-grabbing">
                                                <GripVertical className="h-4 w-4 text-gray-400" />
                                                <span className="min-w-[20px] text-sm font-medium text-gray-600">{index + 1}</span>
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-sm font-medium">{getFieldLabel(field, fieldsInfo)}</span>
                                                    <Badge variant="outline" className="text-xs">
                                                        {getFieldType(field, fieldsInfo)}
                                                    </Badge>
                                                    {fieldsInfo && fieldsInfo[field]?.required && (
                                                        <Badge variant="destructive" className="text-xs">
                                                            Required
                                                        </Badge>
                                                    )}
                                                    {fieldsInfo && fieldsInfo[field]?.read_only && (
                                                        <Badge variant="secondary" className="text-xs">
                                                            Read only
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="truncate text-xs text-gray-500">{field}</p>
                                            </div>

                                            <Button variant="ghost" size="sm" onClick={() => toggleColumnVisibility(field)} className="h-8 w-8 p-0">
                                                <EyeOff className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}

                                    {tempVisibleColumns.length === 0 && (
                                        <div className="py-8 text-center text-gray-500">
                                            <Eye className="mx-auto mb-2 h-8 w-8 opacity-50" />
                                            <p>No column selected</p>
                                            <p className="text-xs">Select fields on the left</p>
                                        </div>
                                    )}
                                </div>
                            </ScrollArea>
                        </div>
                    </div>
                    <div className="flex items-end justify-end gap-4">
                        <Button variant="outline" onClick={() => setTempVisibleColumns([])} disabled={tempVisibleColumns.length === 0}>
                            Unselect all
                        </Button>
                        <Button variant="outline" onClick={() => setTempVisibleColumns(defaultVisibleFields || ["name", "id", "created_at"])}>
                            Default columns
                        </Button>
                        <Button variant="outline" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button variant="default" onClick={handleValidate}>
                            Validate
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
